import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import {
  AppRole,
  resolveOpenAiPricingModelId,
  type ILlmUsageEvent,
  type ILlmUsagePricingRow,
} from "@monorepo/core";

import {
  ADMIN_SIDEBAR_DESKTOP_WIDTH,
  default as AdminSidebar,
} from "../components/admin-sidebar.component";
import AppHeader from "../components/app-header.component";
import LlmUsageChart, {
  LLM_USAGE_METRIC_OPTIONS,
  type LlmUsageMetricKey,
} from "../components/llm-usage-chart.component";
import useAuth from "../hooks/auth.hook";
import {
  useTaxonomyLlmPricingTable,
  useTaxonomyLlmUsageEventDebugArtifact,
  useTaxonomyLlmUsageEvents,
  useTaxonomyLlmUsageEventsPage,
  useUpdateTaxonomyLlmPricingTable,
} from "../hooks/llm-usage.hook";
import { displayFontFamily } from "../theme/mtgTheme";
import { trpc, type RouterInputs } from "../utils/trpc";

const DEFAULT_GRAPH_FIELDS: LlmUsageMetricKey[] = [
  "eventCount",
  "inputTokenCount",
  "outputTokenCount",
];

const TIME_WINDOWS = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All loaded events" },
] as const;

type TimeWindow = (typeof TIME_WINDOWS)[number]["value"];
type LlmUsageEventsPageInput =
  RouterInputs["taxonomy"]["listLlmUsageEventsPage"];
type LlmUsageSummary = {
  totalEvents: number;
  failedEvents: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostMicros: number;
};

const EMPTY_LLM_USAGE_SUMMARY: LlmUsageSummary = {
  totalEvents: 0,
  failedEvents: 0,
  inputTokens: 0,
  outputTokens: 0,
  estimatedCostMicros: 0,
};

const SummaryCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontFamily: displayFontFamily }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const formatMicrosAsUsd = (value: number) =>
  `$${(value / 1_000_000).toFixed(4)}`;

const formatUsdPerMillionTokens = (value: number) =>
  `$${value.toFixed(value >= 1 ? 2 : 3)}`;

const toDistinctSortedValues = (values: readonly string[]) =>
  Array.from(new Set(values.filter((value) => value.length > 0))).sort((a, b) =>
    a.localeCompare(b),
  );

const getWindowStart = (timeWindow: TimeWindow) => {
  if (timeWindow === "all") {
    return undefined;
  }

  const now = Date.now();
  const windowMs =
    timeWindow === "24h"
      ? 24 * 60 * 60 * 1000
      : timeWindow === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000;

  return new Date(now - windowMs).toISOString();
};

const getMonthToDateStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
};

const matchesMultiFilter = (
  value: string | undefined,
  selectedValues: readonly string[],
) =>
  selectedValues.length === 0 ||
  Boolean(value && selectedValues.includes(value));

const renderFilterAutocomplete = ({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly string[];
  value: readonly string[];
  onChange: (value: string[]) => void;
}) => (
  <Autocomplete
    multiple
    size="small"
    options={options}
    value={[...value]}
    onChange={(_event, nextValue) => onChange(nextValue)}
    renderInput={(params) => <TextField {...params} label={label} />}
  />
);

const formatEventStatus = (status: ILlmUsageEvent["eventStatus"]) =>
  status === "COMPLETED" ? "Completed" : "Failed";

const formatUsageTimestamp = (timestamp: string) =>
  new Date(timestamp).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const renderRecentEventMetric = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

const formatDebugArtifactJson = (value: unknown) =>
  JSON.stringify(value, null, 2);

const DebugArtifactPanel = ({
  title,
  value,
}: {
  title: string;
  value: string;
}) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: "background.default",
          fontFamily: "monospace",
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowX: "auto",
        }}
      >
        {value}
      </Box>
    </CardContent>
  </Card>
);

const estimateEventCostMicrosFromPricingRow = ({
  event,
  pricingRow,
}: {
  event: ILlmUsageEvent;
  pricingRow?: ILlmUsagePricingRow;
}) => {
  if (!pricingRow) {
    return event.estimatedCostMicros ?? 0;
  }

  const totalInputTokens = Math.max(0, event.inputTokenCount ?? 0);
  const cachedTokens = Math.min(
    Math.max(0, event.cachedInputTokenCount ?? 0),
    totalInputTokens,
  );
  const uncachedInputTokens = totalInputTokens - cachedTokens;
  const outputTokens = Math.max(0, event.outputTokenCount ?? 0);
  const microsPerUsd = 1_000_000;
  const pricingUnit = 1_000_000;

  return Math.round(
    ((uncachedInputTokens * pricingRow.inputUsdPerMillionTokens +
      cachedTokens * pricingRow.cachedInputUsdPerMillionTokens +
      outputTokens * pricingRow.outputUsdPerMillionTokens) *
      microsPerUsd) /
      pricingUnit,
  );
};

const findPricingRowForModelId = ({
  modelId,
  pricingRowsByModelId,
}: {
  modelId: string;
  pricingRowsByModelId: ReadonlyMap<string, ILlmUsagePricingRow>;
}) => {
  const resolvedModelId = resolveOpenAiPricingModelId({
    modelId,
    candidateModelIds: Array.from(pricingRowsByModelId.keys()),
  });

  return resolvedModelId
    ? pricingRowsByModelId.get(resolvedModelId)
    : undefined;
};

interface IPricingDraftRow {
  inputUsdPerMillionTokens: string;
  cachedInputUsdPerMillionTokens: string;
  outputUsdPerMillionTokens: string;
}

const buildPricingDraftRows = (rows: readonly ILlmUsagePricingRow[]) =>
  Object.fromEntries(
    rows.map((row) => [
      row.modelId,
      {
        inputUsdPerMillionTokens: row.inputUsdPerMillionTokens.toString(),
        cachedInputUsdPerMillionTokens:
          row.cachedInputUsdPerMillionTokens.toString(),
        outputUsdPerMillionTokens: row.outputUsdPerMillionTokens.toString(),
      } satisfies IPricingDraftRow,
    ]),
  ) as Record<string, IPricingDraftRow>;

const AdminPage: FC = () => {
  const { isAdmin, setViewRole, viewRole } = useAuth();
  const trpcUtils = trpc.useUtils();
  const usageEventsQuery = useTaxonomyLlmUsageEvents();
  const pricingTableQuery = useTaxonomyLlmPricingTable();
  const updatePricingMutation = useUpdateTaxonomyLlmPricingTable();
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("7d");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedWorkloads, setSelectedWorkloads] = useState<string[]>([]);
  const [selectedRouteClasses, setSelectedRouteClasses] = useState<string[]>(
    [],
  );
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<string[]>([]);
  const [selectedRunIds, setSelectedRunIds] = useState<string[]>([]);
  const [graphMetrics, setGraphMetrics] =
    useState<LlmUsageMetricKey[]>(DEFAULT_GRAPH_FIELDS);
  const [recentEventsPageIndex, setRecentEventsPageIndex] = useState(0);
  const [recentEventCursors, setRecentEventCursors] = useState<
    (string | undefined)[]
  >([undefined]);
  const [recentEventsRowsPerPage, setRecentEventsRowsPerPage] = useState(12);
  const [selectedDebugEvent, setSelectedDebugEvent] =
    useState<ILlmUsageEvent | null>(null);
  const [pricingDraftRows, setPricingDraftRows] = useState<
    Record<string, IPricingDraftRow>
  >({});
  const [mtdSummary, setMtdSummary] = useState<LlmUsageSummary>(
    EMPTY_LLM_USAGE_SUMMARY,
  );
  const [isMtdSummaryLoading, setIsMtdSummaryLoading] = useState(false);
  const [mtdSummaryError, setMtdSummaryError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin && viewRole !== AppRole.Admin) {
      setViewRole(AppRole.Admin);
    }
  }, [isAdmin, setViewRole, viewRole]);

  const allEvents = usageEventsQuery.data ?? [];
  const pricingRows = pricingTableQuery.data?.rows ?? [];
  const editablePricingRows = useMemo(
    () => pricingRows.filter((pricingRow) => pricingRow.isFallback),
    [pricingRows],
  );
  const isPricingTableEditable = editablePricingRows.length > 0;
  const pricingRowsByModelId = useMemo(
    () =>
      new Map(
        pricingRows.map((pricingRow) => [pricingRow.modelId, pricingRow]),
      ),
    [pricingRows],
  );

  useEffect(() => {
    if (!pricingTableQuery.data) {
      return;
    }

    setPricingDraftRows(buildPricingDraftRows(pricingTableQuery.data.rows));
  }, [pricingTableQuery.data?.refreshedAt, pricingTableQuery.data?.isFallback]);

  const hasInvalidPricingDraft = useMemo(
    () =>
      editablePricingRows.some((row) => {
        const draft = pricingDraftRows[row.modelId];
        if (!draft) {
          return true;
        }

        return [
          draft.inputUsdPerMillionTokens,
          draft.cachedInputUsdPerMillionTokens,
          draft.outputUsdPerMillionTokens,
        ].some((value) => {
          const parsed = Number(value);
          return !Number.isFinite(parsed) || parsed < 0;
        });
      }),
    [editablePricingRows, pricingDraftRows],
  );

  const filterOptions = useMemo(
    () => ({
      statuses: toDistinctSortedValues(
        allEvents.map((event) => event.eventStatus),
      ),
      workloads: toDistinctSortedValues(
        allEvents.map((event) => event.workload),
      ),
      routeClasses: toDistinctSortedValues(
        allEvents.map((event) => event.routeClass),
      ),
      modelIds: toDistinctSortedValues(allEvents.map((event) => event.modelId)),
      entityTypes: toDistinctSortedValues(
        allEvents.map((event) => event.entityType),
      ),
      runIds: toDistinctSortedValues(
        allEvents
          .map((event) => event.runId ?? "")
          .filter((runId) => runId.length > 0),
      ).slice(0, 40),
    }),
    [allEvents],
  );
  const currentRecentEventsCursor = recentEventCursors[recentEventsPageIndex];
  const monthToDateWindowStart = useMemo(() => getMonthToDateStart(), []);
  const recentEventsWindowStart = useMemo(
    () => getWindowStart(timeWindow),
    [timeWindow],
  );
  const monthToDateQueryInput = useMemo(
    (): LlmUsageEventsPageInput => ({
      pageSize: 100,
      eventStatuses:
        selectedStatuses.length > 0
          ? (selectedStatuses as LlmUsageEventsPageInput["eventStatuses"])
          : undefined,
      workloads:
        selectedWorkloads.length > 0
          ? (selectedWorkloads as LlmUsageEventsPageInput["workloads"])
          : undefined,
      routeClasses:
        selectedRouteClasses.length > 0
          ? (selectedRouteClasses as LlmUsageEventsPageInput["routeClasses"])
          : undefined,
      modelIds: selectedModelIds.length > 0 ? selectedModelIds : undefined,
      entityTypes:
        selectedEntityTypes.length > 0
          ? (selectedEntityTypes as LlmUsageEventsPageInput["entityTypes"])
          : undefined,
      runIds: selectedRunIds.length > 0 ? selectedRunIds : undefined,
      createdAfter: monthToDateWindowStart,
    }),
    [
      monthToDateWindowStart,
      selectedEntityTypes,
      selectedModelIds,
      selectedRouteClasses,
      selectedRunIds,
      selectedStatuses,
      selectedWorkloads,
    ],
  );
  const recentEventsQueryInput = useMemo(
    (): LlmUsageEventsPageInput => ({
      cursor: currentRecentEventsCursor,
      pageSize: recentEventsRowsPerPage,
      eventStatuses:
        selectedStatuses.length > 0
          ? (selectedStatuses as LlmUsageEventsPageInput["eventStatuses"])
          : undefined,
      workloads:
        selectedWorkloads.length > 0
          ? (selectedWorkloads as LlmUsageEventsPageInput["workloads"])
          : undefined,
      routeClasses:
        selectedRouteClasses.length > 0
          ? (selectedRouteClasses as LlmUsageEventsPageInput["routeClasses"])
          : undefined,
      modelIds: selectedModelIds.length > 0 ? selectedModelIds : undefined,
      entityTypes:
        selectedEntityTypes.length > 0
          ? (selectedEntityTypes as LlmUsageEventsPageInput["entityTypes"])
          : undefined,
      runIds: selectedRunIds.length > 0 ? selectedRunIds : undefined,
      createdAfter: recentEventsWindowStart,
    }),
    [
      currentRecentEventsCursor,
      recentEventsRowsPerPage,
      recentEventsWindowStart,
      selectedEntityTypes,
      selectedModelIds,
      selectedRouteClasses,
      selectedRunIds,
      selectedStatuses,
      selectedWorkloads,
    ],
  );
  const recentEventsPageQuery = useTaxonomyLlmUsageEventsPage(
    recentEventsQueryInput,
  );
  const selectedDebugUsageEventId = selectedDebugEvent?.usageEventId;
  const debugArtifactQuery = useTaxonomyLlmUsageEventDebugArtifact(
    {
      usageEventId: selectedDebugUsageEventId ?? "__disabled__",
    },
    {
      enabled: Boolean(selectedDebugUsageEventId),
    },
  );

  useEffect(() => {
    let cancelled = false;

    const loadMonthToDateSummary = async () => {
      setIsMtdSummaryLoading(true);
      setMtdSummaryError(null);

      try {
        let cursor: string | undefined;
        const nextSummary = {
          ...EMPTY_LLM_USAGE_SUMMARY,
        };

        do {
          const page = await trpcUtils.taxonomy.listLlmUsageEventsPage.fetch({
            ...monthToDateQueryInput,
            cursor,
          });

          page.items.forEach((event) => {
            nextSummary.totalEvents += 1;
            nextSummary.failedEvents += event.eventStatus === "FAILED" ? 1 : 0;
            nextSummary.inputTokens += event.inputTokenCount ?? 0;
            nextSummary.outputTokens += event.outputTokenCount ?? 0;
            nextSummary.estimatedCostMicros += event.estimatedCostMicros ?? 0;
          });
          cursor = page.nextCursor;
        } while (cursor);

        if (!cancelled) {
          setMtdSummary(nextSummary);
        }
      } catch (error) {
        if (!cancelled) {
          setMtdSummary(EMPTY_LLM_USAGE_SUMMARY);
          setMtdSummaryError(
            error instanceof Error
              ? error.message
              : "Failed to load month-to-date LLM usage.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsMtdSummaryLoading(false);
        }
      }
    };

    void loadMonthToDateSummary();

    return () => {
      cancelled = true;
    };
  }, [monthToDateQueryInput, trpcUtils]);

  const filteredEvents = useMemo(() => {
    return allEvents
      .filter((event) =>
        recentEventsWindowStart
          ? event.createdAt >= recentEventsWindowStart
          : true,
      )
      .filter((event) =>
        matchesMultiFilter(event.eventStatus, selectedStatuses),
      )
      .filter((event) => matchesMultiFilter(event.workload, selectedWorkloads))
      .filter((event) =>
        matchesMultiFilter(event.routeClass, selectedRouteClasses),
      )
      .filter((event) => matchesMultiFilter(event.modelId, selectedModelIds))
      .filter((event) =>
        matchesMultiFilter(event.entityType, selectedEntityTypes),
      )
      .filter((event) => matchesMultiFilter(event.runId, selectedRunIds));
  }, [
    allEvents,
    selectedEntityTypes,
    selectedModelIds,
    selectedRouteClasses,
    selectedRunIds,
    selectedStatuses,
    selectedWorkloads,
    recentEventsWindowStart,
  ]);

  const displayEvents = useMemo(
    () =>
      filteredEvents.map((event) => ({
        ...event,
        estimatedCostMicros: estimateEventCostMicrosFromPricingRow({
          event,
          pricingRow: findPricingRowForModelId({
            modelId: event.modelId,
            pricingRowsByModelId,
          }),
        }),
      })),
    [filteredEvents, pricingRowsByModelId],
  );

  useEffect(() => {
    setRecentEventsPageIndex(0);
    setRecentEventCursors([undefined]);
  }, [
    recentEventsRowsPerPage,
    timeWindow,
    selectedStatuses,
    selectedWorkloads,
    selectedRouteClasses,
    selectedModelIds,
    selectedEntityTypes,
    selectedRunIds,
  ]);
  const recentEventsPageData = recentEventsPageQuery.data;
  const hasNextRecentEventsPage = Boolean(recentEventsPageData?.nextCursor);
  const recentEventItems = recentEventsPageData?.items ?? [];
  const recentEvents = useMemo(
    () =>
      recentEventItems.map((event) => ({
        ...event,
        estimatedCostMicros: estimateEventCostMicrosFromPricingRow({
          event,
          pricingRow: findPricingRowForModelId({
            modelId: event.modelId,
            pricingRowsByModelId,
          }),
        }),
      })),
    [pricingRowsByModelId, recentEventItems],
  );

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <AppHeader />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: { xs: "flex", lg: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            lg: `${ADMIN_SIDEBAR_DESKTOP_WIDTH}px minmax(0, 1fr)`,
          },
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        <AdminSidebar activeView="llm-usage" />
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Stack spacing={3} sx={{ minWidth: 0 }}>
              <Box>
                <Typography
                  variant="h3"
                  sx={{ fontFamily: displayFontFamily, mb: 1 }}
                >
                  LLM Usage
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Inspect taxonomy bootstrap and alias-sync model usage, filter
                  the event stream, and chart the signals that matter.
                </Typography>
              </Box>

              {usageEventsQuery.isLoading ? (
                <Alert severity="info">Loading LLM usage events...</Alert>
              ) : null}
              {usageEventsQuery.error ? (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => void usageEventsQuery.refetch()}
                    >
                      Retry
                    </Button>
                  }
                >
                  {usageEventsQuery.error.message}
                </Alert>
              ) : null}
              {pricingTableQuery.error ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => void pricingTableQuery.refetch()}
                    >
                      Retry
                    </Button>
                  }
                >
                  {pricingTableQuery.error.message}
                </Alert>
              ) : null}
              {mtdSummaryError ? (
                <Alert severity="warning">{mtdSummaryError}</Alert>
              ) : null}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <SummaryCard
                  label="Events"
                  value={
                    isMtdSummaryLoading
                      ? "..."
                      : mtdSummary.totalEvents.toLocaleString()
                  }
                  helper={
                    isMtdSummaryLoading
                      ? "Loading month-to-date totals..."
                      : `${mtdSummary.failedEvents.toLocaleString()} failed month to date`
                  }
                />
                <SummaryCard
                  label="Input Tokens"
                  value={
                    isMtdSummaryLoading
                      ? "..."
                      : mtdSummary.inputTokens.toLocaleString()
                  }
                  helper="Month-to-date across current filters"
                />
                <SummaryCard
                  label="Output Tokens"
                  value={
                    isMtdSummaryLoading
                      ? "..."
                      : mtdSummary.outputTokens.toLocaleString()
                  }
                  helper="Month-to-date across current filters"
                />
                <SummaryCard
                  label="Estimated Cost"
                  value={
                    isMtdSummaryLoading
                      ? "..."
                      : formatMicrosAsUsd(mtdSummary.estimatedCostMicros)
                  }
                  helper={
                    pricingTableQuery.data
                      ? "Month-to-date using the current pricing table"
                      : "Month-to-date using stored event estimates"
                  }
                />
              </Box>

              <Card>
                <CardContent>
                  <Stack spacing={2.5}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={2}
                      alignItems={{ xs: "stretch", md: "flex-start" }}
                    >
                      <Box>
                        <Typography variant="h6">Pricing Table</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Admin-managed pricing used for the cost displays on
                          this page.
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {pricingTableQuery.data
                          ? `Last updated ${formatUsageTimestamp(
                              pricingTableQuery.data.refreshedAt,
                            )}`
                          : "Pricing not loaded yet"}
                      </Typography>
                    </Stack>

                    {pricingTableQuery.isLoading ? (
                      <Alert severity="info">Loading pricing table...</Alert>
                    ) : null}

                    {updatePricingMutation.error ? (
                      <Alert severity="error">
                        {updatePricingMutation.error.message}
                      </Alert>
                    ) : null}

                    {pricingRows.length === 0 ? (
                      <Alert severity="info">
                        No pricing rows are currently available.
                      </Alert>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Model</TableCell>
                            <TableCell>Input / 1M</TableCell>
                            <TableCell>Cached Input / 1M</TableCell>
                            <TableCell>Output / 1M</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pricingRows.map((pricingRow) => (
                            <TableRow key={pricingRow.modelId} hover>
                              <TableCell>
                                <Stack spacing={0.25}>
                                  <Typography variant="body2">
                                    {pricingRow.displayName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {pricingRow.modelId}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                {pricingRow.isFallback ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={
                                      pricingDraftRows[pricingRow.modelId]
                                        ?.inputUsdPerMillionTokens ?? ""
                                    }
                                    onChange={(event) =>
                                      setPricingDraftRows((current) => ({
                                        ...current,
                                        [pricingRow.modelId]: {
                                          ...current[pricingRow.modelId],
                                          inputUsdPerMillionTokens:
                                            event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                ) : (
                                  formatUsdPerMillionTokens(
                                    pricingRow.inputUsdPerMillionTokens,
                                  )
                                )}
                              </TableCell>
                              <TableCell>
                                {pricingRow.isFallback ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={
                                      pricingDraftRows[pricingRow.modelId]
                                        ?.cachedInputUsdPerMillionTokens ?? ""
                                    }
                                    onChange={(event) =>
                                      setPricingDraftRows((current) => ({
                                        ...current,
                                        [pricingRow.modelId]: {
                                          ...current[pricingRow.modelId],
                                          cachedInputUsdPerMillionTokens:
                                            event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                ) : (
                                  formatUsdPerMillionTokens(
                                    pricingRow.cachedInputUsdPerMillionTokens,
                                  )
                                )}
                              </TableCell>
                              <TableCell>
                                {pricingRow.isFallback ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={
                                      pricingDraftRows[pricingRow.modelId]
                                        ?.outputUsdPerMillionTokens ?? ""
                                    }
                                    onChange={(event) =>
                                      setPricingDraftRows((current) => ({
                                        ...current,
                                        [pricingRow.modelId]: {
                                          ...current[pricingRow.modelId],
                                          outputUsdPerMillionTokens:
                                            event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                ) : (
                                  formatUsdPerMillionTokens(
                                    pricingRow.outputUsdPerMillionTokens,
                                  )
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {isPricingTableEditable ? (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Pricing values are edited manually and persisted in
                          Dynamo.
                        </Typography>
                        <Button
                          variant="contained"
                          disabled={
                            hasInvalidPricingDraft ||
                            updatePricingMutation.isLoading
                          }
                          onClick={() =>
                            void updatePricingMutation.mutateAsync({
                              rows: editablePricingRows.map((row) => ({
                                modelId: row.modelId,
                                inputUsdPerMillionTokens: Number(
                                  pricingDraftRows[row.modelId]
                                    ?.inputUsdPerMillionTokens ?? 0,
                                ),
                                cachedInputUsdPerMillionTokens: Number(
                                  pricingDraftRows[row.modelId]
                                    ?.cachedInputUsdPerMillionTokens ?? 0,
                                ),
                                outputUsdPerMillionTokens: Number(
                                  pricingDraftRows[row.modelId]
                                    ?.outputUsdPerMillionTokens ?? 0,
                                ),
                              })),
                            })
                          }
                        >
                          {updatePricingMutation.isLoading
                            ? "Saving..."
                            : "Save Pricing"}
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Stack spacing={2.5}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="h6">Filters</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Narrow the usage stream by event properties and choose
                          which metrics to graph.
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setTimeWindow("7d");
                          setSelectedStatuses([]);
                          setSelectedWorkloads([]);
                          setSelectedRouteClasses([]);
                          setSelectedModelIds([]);
                          setSelectedEntityTypes([]);
                          setSelectedRunIds([]);
                          setGraphMetrics(DEFAULT_GRAPH_FIELDS);
                        }}
                      >
                        Reset Filters
                      </Button>
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(2, minmax(0, 1fr))",
                          xl: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 2,
                      }}
                    >
                      <TextField
                        select
                        label="Time Window"
                        size="small"
                        value={timeWindow}
                        onChange={(event) =>
                          setTimeWindow(event.target.value as TimeWindow)
                        }
                      >
                        {TIME_WINDOWS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {renderFilterAutocomplete({
                        label: "Event Status",
                        options: filterOptions.statuses,
                        value: selectedStatuses,
                        onChange: setSelectedStatuses,
                      })}
                      {renderFilterAutocomplete({
                        label: "Workload",
                        options: filterOptions.workloads,
                        value: selectedWorkloads,
                        onChange: setSelectedWorkloads,
                      })}
                      {renderFilterAutocomplete({
                        label: "Route Class",
                        options: filterOptions.routeClasses,
                        value: selectedRouteClasses,
                        onChange: setSelectedRouteClasses,
                      })}
                      {renderFilterAutocomplete({
                        label: "Model",
                        options: filterOptions.modelIds,
                        value: selectedModelIds,
                        onChange: setSelectedModelIds,
                      })}
                      {renderFilterAutocomplete({
                        label: "Entity Type",
                        options: filterOptions.entityTypes,
                        value: selectedEntityTypes,
                        onChange: setSelectedEntityTypes,
                      })}
                      {renderFilterAutocomplete({
                        label: "Run Id",
                        options: filterOptions.runIds,
                        value: selectedRunIds,
                        onChange: setSelectedRunIds,
                      })}
                      <Autocomplete
                        multiple
                        size="small"
                        options={LLM_USAGE_METRIC_OPTIONS}
                        value={LLM_USAGE_METRIC_OPTIONS.filter((option) =>
                          graphMetrics.includes(option.value),
                        )}
                        onChange={(_event, nextValue) =>
                          setGraphMetrics(
                            nextValue.map((option) => option.value),
                          )
                        }
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(left, right) =>
                          left.value === right.value
                        }
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              label={option.label}
                              size="small"
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Graph Fields" />
                        )}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Usage Graphs
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2.5 }}
                  >
                    Events are bucketed automatically by hour or day depending
                    on the active time range.
                  </Typography>
                  <LlmUsageChart
                    events={displayEvents}
                    metrics={graphMetrics}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Recent Events
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2.5 }}
                      >
                        The latest usage receipts after filters are applied.
                        Inspect is available when a run captured prompt/output
                        debug artifacts.
                      </Typography>
                    </Box>
                    {recentEventsPageQuery.error ? (
                      <Alert
                        severity="error"
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => void recentEventsPageQuery.refetch()}
                          >
                            Retry
                          </Button>
                        }
                      >
                        {recentEventsPageQuery.error.message}
                      </Alert>
                    ) : null}
                    {recentEventsPageQuery.isLoading ? (
                      <Alert severity="info">Loading recent events...</Alert>
                    ) : null}
                    {!recentEventsPageQuery.isLoading &&
                    !recentEventsPageQuery.error &&
                    recentEvents.length === 0 ? (
                      <Alert severity="info">
                        No events match the current filters.
                      </Alert>
                    ) : (
                      <Stack spacing={1}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Time</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Model</TableCell>
                              <TableCell>Workload</TableCell>
                              <TableCell>Entity</TableCell>
                              <TableCell>Usage</TableCell>
                              <TableCell>Debug</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {recentEvents.map((event) => (
                              <TableRow key={event.usageEventId} hover>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {formatUsageTimestamp(event.createdAt)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={formatEventStatus(event.eventStatus)}
                                    color={
                                      event.eventStatus === "COMPLETED"
                                        ? "success"
                                        : "error"
                                    }
                                    variant={
                                      event.eventStatus === "COMPLETED"
                                        ? "filled"
                                        : "outlined"
                                    }
                                  />
                                </TableCell>
                                <TableCell>{event.modelId}</TableCell>
                                <TableCell>{event.workload}</TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    <Typography variant="body2">
                                      {event.entityType}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ wordBreak: "break-all" }}
                                    >
                                      {event.entityId}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.25}>
                                    {renderRecentEventMetric({
                                      label: "In",
                                      value: (
                                        event.inputTokenCount ?? 0
                                      ).toLocaleString(),
                                    })}
                                    {renderRecentEventMetric({
                                      label: "Out",
                                      value: (
                                        event.outputTokenCount ?? 0
                                      ).toLocaleString(),
                                    })}
                                    {renderRecentEventMetric({
                                      label: "Cost",
                                      value: formatMicrosAsUsd(
                                        event.estimatedCostMicros ?? 0,
                                      ),
                                    })}
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {event.debugArtifact ? (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        setSelectedDebugEvent(event)
                                      }
                                    >
                                      Inspect
                                    </Button>
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          <TextField
                            select
                            size="small"
                            label="Rows per page"
                            value={recentEventsRowsPerPage}
                            onChange={(event) =>
                              setRecentEventsRowsPerPage(
                                Number(event.target.value),
                              )
                            }
                            sx={{ minWidth: 140 }}
                          >
                            {[12, 25, 50, 100].map((pageSize) => (
                              <MenuItem key={pageSize} value={pageSize}>
                                {pageSize}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Button
                              variant="outlined"
                              onClick={() =>
                                setRecentEventsPageIndex((currentPage) =>
                                  Math.max(0, currentPage - 1),
                                )
                              }
                              disabled={
                                recentEventsPageIndex === 0 ||
                                recentEventsPageQuery.isFetching
                              }
                            >
                              Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              Page {recentEventsPageIndex + 1}
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                const nextCursor =
                                  recentEventsPageData?.nextCursor;
                                if (!nextCursor) {
                                  return;
                                }

                                setRecentEventCursors((currentCursors) => {
                                  const nextCursors = currentCursors.slice(
                                    0,
                                    recentEventsPageIndex + 1,
                                  );
                                  nextCursors.push(nextCursor);
                                  return nextCursors;
                                });
                                setRecentEventsPageIndex(
                                  (currentPage) => currentPage + 1,
                                );
                              }}
                              disabled={
                                !hasNextRecentEventsPage ||
                                recentEventsPageQuery.isFetching
                              }
                            >
                              Next
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
            <Dialog
              open={Boolean(selectedDebugEvent)}
              onClose={() => setSelectedDebugEvent(null)}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle>LLM Debug Artifact</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  {selectedDebugEvent ? (
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        {selectedDebugEvent.modelId} ·{" "}
                        {selectedDebugEvent.workload} ·{" "}
                        {selectedDebugEvent.entityType}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all" }}
                      >
                        Event {selectedDebugEvent.usageEventId} ·{" "}
                        {selectedDebugEvent.entityId}
                      </Typography>
                      {selectedDebugEvent.debugArtifact ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {selectedDebugEvent.debugArtifact.storageKey}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                  {debugArtifactQuery.isLoading ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ py: 4 }}
                    >
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading prompt and output artifact...
                      </Typography>
                    </Stack>
                  ) : debugArtifactQuery.error ? (
                    <Alert severity="error">
                      {debugArtifactQuery.error.message}
                    </Alert>
                  ) : !debugArtifactQuery.data ? (
                    <Alert severity="info">
                      No debug artifact is stored for this usage event.
                    </Alert>
                  ) : (
                    <>
                      <DebugArtifactPanel
                        title="Prompt"
                        value={debugArtifactQuery.data.prompt}
                      />
                      {debugArtifactQuery.data.structuredOutputText ? (
                        <DebugArtifactPanel
                          title="Structured Output Text"
                          value={debugArtifactQuery.data.structuredOutputText}
                        />
                      ) : null}
                      {debugArtifactQuery.data.parsedOutput ? (
                        <DebugArtifactPanel
                          title="Parsed Output JSON"
                          value={formatDebugArtifactJson(
                            debugArtifactQuery.data.parsedOutput,
                          )}
                        />
                      ) : null}
                      {debugArtifactQuery.data.failureReason ||
                      debugArtifactQuery.data.failureDetail ? (
                        <DebugArtifactPanel
                          title="Failure Detail"
                          value={formatDebugArtifactJson({
                            failureReason:
                              debugArtifactQuery.data.failureReason,
                            failureDetail:
                              debugArtifactQuery.data.failureDetail,
                          })}
                        />
                      ) : null}
                      {debugArtifactQuery.data.providerResponse ? (
                        <DebugArtifactPanel
                          title="Provider Response"
                          value={formatDebugArtifactJson(
                            debugArtifactQuery.data.providerResponse,
                          )}
                        />
                      ) : null}
                    </>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedDebugEvent(null)}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPage;
